/**
 * 画布导出：浏览器端将视频轨 + 音频轨合成 WebM（MediaRecorder）。
 * 不依赖 ffmpeg.wasm；CORS 友好 URL 经 credentials fetch → blob URL。
 */
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'

export async function fetchMediaBlob(url: string): Promise<Blob> {
  const display = resolveMediaDisplayUrl(url) || url
  const res = await fetch(display, { credentials: 'include' })
  if (!res.ok) throw new Error(`media_fetch_${res.status}`)
  return res.blob()
}

function pickRecorderMime(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ]
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m
  }
  return 'video/webm'
}

function waitEvent(el: HTMLMediaElement, type: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const onOk = () => {
      cleanup()
      resolve()
    }
    const onErr = () => {
      cleanup()
      reject(new Error(`media_${type}_error`))
    }
    const cleanup = () => {
      el.removeEventListener(type, onOk)
      el.removeEventListener('error', onErr)
    }
    el.addEventListener(type, onOk, { once: true })
    el.addEventListener('error', onErr, { once: true })
  })
}

/**
 * 将 videoUrl + audioUrl 合成为 WebM Blob（时长以视频为准）。
 */
export async function muxVideoWithAudio(
  videoUrl: string,
  audioUrl: string,
  opts?: { onProgress?: (ratio: number) => void },
): Promise<Blob> {
  if (typeof MediaRecorder === 'undefined' || typeof HTMLVideoElement === 'undefined') {
    throw new Error('media_recorder_unsupported')
  }

  const [videoBlob, audioBlob] = await Promise.all([
    fetchMediaBlob(videoUrl),
    fetchMediaBlob(audioUrl),
  ])
  const videoObjectUrl = URL.createObjectURL(videoBlob)
  const audioObjectUrl = URL.createObjectURL(audioBlob)

  const video = document.createElement('video')
  video.src = videoObjectUrl
  video.crossOrigin = 'anonymous'
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'

  const audio = document.createElement('audio')
  audio.src = audioObjectUrl
  audio.crossOrigin = 'anonymous'
  audio.preload = 'auto'

  try {
    await Promise.all([
      waitEvent(video, 'loadedmetadata'),
      waitEvent(audio, 'loadedmetadata'),
    ])

    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0
    if (duration <= 0) throw new Error('video_duration_invalid')

    const capture = (
      video as HTMLVideoElement & { captureStream?: (frameRate?: number) => MediaStream }
    ).captureStream?.bind(video)
    if (!capture) throw new Error('capture_stream_unsupported')

    const videoStream = capture(30)
    const audioCtx = new AudioContext()
    const audioSource = audioCtx.createMediaElementSource(audio)
    const dest = audioCtx.createMediaStreamDestination()
    audioSource.connect(dest)

    const combined = new MediaStream([
      ...videoStream.getVideoTracks(),
      ...dest.stream.getAudioTracks(),
    ])

    const mimeType = pickRecorderMime()
    const chunks: BlobPart[] = []
    const recorder = new MediaRecorder(combined, { mimeType })
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunks.push(ev.data)
    }

    const stopped = new Promise<Blob>((resolve, reject) => {
      recorder.onerror = () => reject(new Error('recorder_error'))
      recorder.onstop = () => {
        resolve(new Blob(chunks, { type: mimeType.split(';')[0] || 'video/webm' }))
      }
    })

    recorder.start(250)
    video.currentTime = 0
    audio.currentTime = 0
    await Promise.all([video.play(), audio.play()])

    await new Promise<void>((resolve) => {
      const tick = () => {
        if (video.ended || video.currentTime >= duration - 0.05) {
          resolve()
          return
        }
        opts?.onProgress?.(Math.min(1, video.currentTime / duration))
        requestAnimationFrame(tick)
      }
      video.addEventListener('ended', () => resolve(), { once: true })
      requestAnimationFrame(tick)
    })

    video.pause()
    audio.pause()
    if (recorder.state !== 'inactive') recorder.stop()
    const out = await stopped
    void audioCtx.close()
    opts?.onProgress?.(1)
    return out
  } finally {
    URL.revokeObjectURL(videoObjectUrl)
    URL.revokeObjectURL(audioObjectUrl)
  }
}
